import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';
// VehicleSchematicCard.jsx
import React, { useState, useCallback, useMemo } from 'react';
import {
  Paper,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  DirectionsCar as VehicleIcon,
  Timeline as TimelineIcon,
  Public as WorldIcon,
  VolumeOff as MuteIcon,
  VolumeUp as UnmuteIcon,
  Assignment as TicketIcon
} from '@mui/icons-material';

import PositionTile from './PositionTile';
import VehicleRouteMapModal from './VehicleRouteMapModal';
import ReceiverEventsModal from './ReceiverEventsModal';
import VehicleTicketsDrawer from './VehicleTicketsDrawer';
import VehicleOperatorsButton from '../operators/VehicleOperatorsButton';
import { useTickets } from '../../hooks/useTickets';
import { useNotification } from '../common/GlobalNotifications';
import { useReceiverEventsMultiple, formatReceiverEventTime, getTimeSinceLastOnline } from '../../hooks/useReceiverEvents';

// Aseguramos que el Tile tenga un tamaño consistente
const StyledPositionTile = (props) => (
  <Box sx={{
    width: { xs: 32, sm: 38 },
    height: { xs: 32, sm: 38 },
    flexShrink: 0,
    flexGrow: 0
  }}>
    <PositionTile {...props} />
  </Box>
);

// Constantes locales
// La bolita del receptor SOLO representa conectividad del receptor.
// 3 estados visuales únicamente: verde (online), gris (offline), gris oscuro (sin dato).
// No representa alertas operativas del vehículo (PSI/TEMP/Dual).
const VEHICLE_STATUS_COLORS = {
  online: 'success',
  offline: 'default',
  unknown: 'default',
};

const STATUS_DOT_COLORS = {
  online: '#4caf50', // verde — receptor reportando
  offline: '#9e9e9e', // gris claro — receptor desconectado
  unknown: '#424242', // gris oscuro — sin dato / sin receptor asignado
};

const VehicleSchematicCard = ({
  vehicle,
  realtimeData = {},
  lastAvailableData = {},
  alarms = [],
  alarmStatuses = [],
  onTelemetryClick,
  globalMute = false,
  vehicleMutes = {},
  onToggleVehicleMute,
  displayMode = 'pressure',
  showCompanyTag = false,
  // Modo "Presión en frío": el plan por posición lo calcula el backend
  // (motor central `tpms_action_plan`), aquí solo se muestra.
  coldMode = false,
  coldPlan = null
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const statusLabels = useMemo(() => ({
    online: t('hardcoded.components_schematics_vehicleschematiccard.online'),
    offline: t('hardcoded.components_schematics_vehicleschematiccard.offline'),
    unknown: t('hardcoded.components_schematics_vehicleschematiccard.sin_dato'),
  }), [t]);
  const [mapModalOpen, setMapModalOpen] = useState(false);
  const [receiverEventsModalOpen, setReceiverEventsModalOpen] = useState(false);
  const [ticketsDrawerOpen, setTicketsDrawerOpen] = useState(false);
  const { showSuccess, showError } = useNotification();

  // Hook de Tickets
  const {
    getActiveTicketsByVehicle,
    acknowledgeVehicleTickets,
    vehicleHasOpenTickets,
    vehicleHasActiveTickets,
    refreshActiveTickets,
  } = useTickets();

  // Obtener IDs de receptores del vehículo
  const receiverIds = useMemo(() => {
    if (!vehicle?.receivers || vehicle.receivers.length === 0) return [];
    return vehicle.receivers.map(r => r.id);
  }, [vehicle?.receivers]);

  // Hook para obtener eventos de los receptores
  const { eventsMap: receiverEventsMap } = useReceiverEventsMultiple(receiverIds);

  // --- 1. HOOKS PRIMERO (Antes de cualquier return) ---

  // Silenciar SOLO silencia el sonido/notificación del vehículo.
  // NO marca tickets como "vistos". Visto y Resolver son acciones explícitas
  // separadas (botones en el sidebar y popup de detalle del ticket).
  const handleToggleMute = useCallback(() => {
    if (!vehicle) return;
    try {
      if (onToggleVehicleMute && !globalMute) {
        onToggleVehicleMute(vehicle.id);
      }
    } catch (error) {
      console.error('Error toggling vehicle mute:', error);
      showError(t('hardcoded.components_schematics_alarmmanager.error_al_silenciar_vehiculo'));
    }
  }, [vehicle, onToggleVehicleMute, globalMute, showError]);

  // --- CORRECCIÓN: useMemo movido AQUÍ, antes del early return ---
  const layout = useMemo(() => {
    // Protección interna: si no hay vehículo, retornar array vacío
    if (!vehicle || !vehicle.positions_count) return [];

    let allPositions;

    if (!vehicle.figure) {
      // Lógica por defecto: filas de 4
      const positions = Array.from({ length: vehicle.positions_count }, (_, i) => i + 1);
      if (positions.length <= 4) {
        allPositions = [positions];
      } else {
        const rows = [];
        for (let i = 0; i < positions.length; i += 4) {
          rows.push(positions.slice(i, i + 4));
        }
        allPositions = rows;
      }
    } else {
      // Lógica de parseo de figura
      const axles = vehicle.figure.split(',').map(axle => axle.trim());
      const computedLayout = [];
      let positionCounter = 1;

      axles.forEach((axleConfig) => {
        const row = [];

        if (axleConfig.includes('-')) {
          const [left, right] = axleConfig.split('-').map(n => parseInt(n.trim(), 10));
          for (let i = 0; i < left; i++) {
            if (positionCounter <= vehicle.positions_count) row.push(positionCounter++);
          }
          for (let i = 0; i < right; i++) {
            if (positionCounter <= vehicle.positions_count) row.push(positionCounter++);
          }
        } else {
          const tiresInAxle = parseInt(axleConfig, 10);
          for (let i = 0; i < tiresInAxle; i++) {
            if (positionCounter <= vehicle.positions_count) row.push(positionCounter++);
          }
        }

        if (row.length > 0) {
          computedLayout.push(row);
        }
      });

      if (positionCounter <= vehicle.positions_count) {
        const remainingRow = [];
        while (positionCounter <= vehicle.positions_count) {
          remainingRow.push(positionCounter++);
        }
        if (remainingRow.length > 0) {
          computedLayout.push(remainingRow);
        }
      }

      allPositions = computedLayout;
    }

    return allPositions;
    // Usamos optional chaining (?.) en las dependencias para evitar crash si vehicle es null
  }, [vehicle?.figure, vehicle?.positions_count, vehicle?.id, vehicle]);

  // --- 2. EARLY RETURN (Ahora es seguro hacerlo) ---
  if (!vehicle) {
    return null;
  }

  // --- 3. CÁLCULOS DERIVADOS (Después del return es seguro porque no son hooks) ---
  const vehicleTickets = getActiveTicketsByVehicle(vehicle.id);
  const vehicleOpenTickets = vehicleTickets.filter(ticket => ticket.status === 'open');
  const vehicleAcknowledgedTickets = vehicleTickets.filter(ticket => ticket.status === 'acknowledged');

  // Grupos Dual activos: lista de arrays de posiciones del mismo grupo.
  // Detecta tanto desde alarmStatuses.active_reasons como desde alarmStatus.alarm_reason.
  const yoyoGroups = (() => {
    const groups = [];
    const seen = new Set();
    const considerEntries = [
      // Razones activas (snapshot)
      ...alarmStatuses
        .filter((st) => st.vehicle_id === vehicle.id)
        .flatMap((st) => {
          const ar = (st.active_reasons || []).filter((r) => r.alarm_reason === 'dual_yoyo');
          if (ar.length) return ar.map((r) => r.group_positions || st.group_positions || []);
          if (st.alarm_reason === 'dual_yoyo') return [st.group_positions || []];
          return [];
        }),
      // Tickets activos (incluye eval metadata.group_positions si está poblado)
      ...vehicleTickets
        .filter((t) => t.alarm_reason === 'dual_yoyo')
        .map((t) => t.group_positions || t.evaluation_metadata?.group_positions || []),
    ];

    considerEntries.forEach((group) => {
      const arr = (group || []).map(Number).filter((n) => Number.isFinite(n));
      if (arr.length < 2) return;
      const key = [...arr].sort((a, b) => a - b).join(',');
      if (seen.has(key)) return;
      seen.add(key);
      groups.push(arr);
    });
    return groups;
  })();

  // Set rápido para saber si una posición pertenece a un grupo Yoyo activo (cualquier grupo).
  const yoyoPositions = new Set(yoyoGroups.flat());

  // Estado del receptor — SOLO conectividad. No depende de alertas del vehículo.
  // Valores: 'online' | 'offline' | 'unknown'
  const getVehicleStatus = () => {
    if (!vehicle.receivers || vehicle.receivers.length === 0) {
      return 'unknown'; // sin receptor asignado → sin dato
    }

    // Si AL MENOS UN receptor está online → verde.
    // Si todos están offline → gris (offline).
    // Si no hay info de ninguno → gris oscuro (unknown).
    let anyOnline = false;
    let anyKnown = false;

    for (const receiver of vehicle.receivers) {
      const receiverEventData = receiverEventsMap[receiver.id];
      if (receiverEventData && receiverEventData.status) {
        anyKnown = true;
        if (receiverEventData.status === 'online') {
          anyOnline = true;
          break;
        }
      }
    }

    if (anyOnline) return 'online';
    if (anyKnown) return 'offline';
    return 'unknown';
  };

  const hasActiveAlarms = () => {
    return vehicleHasActiveTickets(vehicle.id) || alarmStatuses.some(alarmStatus =>
      alarmStatus.vehicle_id === vehicle.id &&
      (alarmStatus.status === 'CRITICAL' || alarmStatus.status === 'WARNING')
    );
  };

  const status = getVehicleStatus();
  const hasAssignedReceivers = !!(vehicle.receivers && vehicle.receivers.length > 0);
  const hasAlarms = hasActiveAlarms();
  const isVehicleMuted = vehicleMutes[vehicle.id] || false;

  const shouldPlaySound = vehicleOpenTickets.length > 0 && !globalMute && !isVehicleMuted;
  const cardWidth = showCompanyTag
    ? { xs: '100%', sm: 248 }
    : { xs: '100%', sm: 'fit-content' };
  const minCardWidth = showCompanyTag
    ? { xs: 'auto', sm: 248 }
    : { xs: 'auto', sm: 200 };

  if (layout.length === 0) return null;

  // Obtener información del receptor principal para mostrar — solo conectividad.
  const getPrimaryReceiverInfo = () => {
    if (!vehicle.receivers || vehicle.receivers.length === 0) {
      return { receiver: null, status: 'unknown', lastOnline: null, events: null };
    }
    // Preferimos el primer receptor que esté online; si ninguno, el primero con info; si no, el primero.
    let primaryReceiver = vehicle.receivers[0];
    let onlineReceiver = null;
    let anyKnownReceiver = null;
    let lastEvents = null;

    for (const receiver of vehicle.receivers) {
      const ev = receiverEventsMap[receiver.id];
      if (ev) {
        anyKnownReceiver = anyKnownReceiver || receiver;
        if (ev.status === 'online') {
          onlineReceiver = receiver;
          lastEvents = ev;
          break;
        }
        lastEvents = lastEvents || ev;
      }
    }

    const selected = onlineReceiver || anyKnownReceiver || primaryReceiver;
    const events = receiverEventsMap[selected?.id] || lastEvents;
    let normalized = 'unknown';
    if (events?.status === 'online') normalized = 'online';
    else if (events?.status) normalized = 'offline'; // warning/offline/cualquier no-online → offline visual

    return { receiver: selected, status: normalized, lastOnline: events?.last_online, events };
  };

  const primaryReceiverInfo = getPrimaryReceiverInfo();

  // Tooltip claro: estado del receptor (sin mezclar alertas del vehículo)
  const getStatusTooltipText = () => {
    if (!primaryReceiverInfo.receiver) return t('hardcoded.components_schematics_vehicleschematiccard.sin_receptor_asignado');
    const statusLabel = statusLabels[primaryReceiverInfo.status] || statusLabels.unknown;
    if (primaryReceiverInfo.lastOnline) {
      const lastOnlineFormatted = formatReceiverEventTime(primaryReceiverInfo.lastOnline);
      return t('hardcoded.components_schematics_vehicleschematiccard.receptor_statuslabel_ultimo_online_lastonlineformatted', { statusLabel: statusLabel, lastOnlineFormatted: lastOnlineFormatted });
    }
    return t('hardcoded.components_schematics_vehicleschematiccard.receptor_statuslabel', { statusLabel: statusLabel });
  };

  // Manejar click en status dot para abrir modal de eventos del receptor
  const handleStatusDotClick = () => {
    if (hasAssignedReceivers) {
      setReceiverEventsModalOpen(true);
    }
  };

  const animationKeyframes = `
    @keyframes alarm-pulse {
      0% { box-shadow: 0 0 0 0 ${theme.palette.error.main}40; }
      70% { box-shadow: 0 0 0 10px ${theme.palette.error.main}00; }
      100% { box-shadow: 0 0 0 0 ${theme.palette.error.main}00; }
    }
    
    @keyframes blink {
      0%, 50% { opacity: 1; }
      51%, 100% { opacity: 0.3; }
    }

    @keyframes status-pulse {
      0% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.2); opacity: 0.8; }
      100% { transform: scale(1); opacity: 1; }
    }
  `;

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: animationKeyframes }} />

      <Paper
        elevation={hasAlarms ? 4 : 1}
        sx={{
          p: { xs: 1, sm: 1.5 },
          height: '100%',
          position: 'relative',
          transition: 'all 0.2s ease',
          borderRadius: 0.75,
          border: vehicleOpenTickets.length > 0 ?
            (globalMute || isVehicleMuted ? `2px solid ${theme.palette.warning.main}` : `2px solid ${theme.palette.error.main}`) :
            vehicleAcknowledgedTickets.length > 0 ? `2px solid ${theme.palette.warning.main}` :
              hasAlarms ? `1px solid ${theme.palette.warning.main}` : 'none',
          animation: shouldPlaySound ? 'alarm-pulse 2s infinite' : 'none',
          width: cardWidth,
          minWidth: minCardWidth,
          maxWidth: { xs: '100%', sm: '100%' },
          minHeight: 150,
          overflow: 'hidden',
          opacity: globalMute || isVehicleMuted || (vehicleAcknowledgedTickets.length > 0 && vehicleOpenTickets.length === 0) ? 0.8 : 1,
          '&:hover': {
            boxShadow: theme.shadows[4],
          },
        }}
      >
        {/* Icono de Operadores asignados (top-left). Siempre visible para que
            el operador en sitio sepa quién maneja la unidad; abre popover con
            slots 1 y 2 y permite cambiar/quitar/asignar si tiene permisos. */}
        <Box sx={{ position: 'absolute', top: 4, left: 4, zIndex: 10 }}>
          <VehicleOperatorsButton vehicleId={vehicle.id} size="small" />
        </Box>

        {/* Indicadores de Tickets */}
        {(vehicleOpenTickets.length > 0 || vehicleAcknowledgedTickets.length > 0) && (
          <Box sx={{
            position: 'absolute',
            top: 8,
            right: 8,
            zIndex: 10,
            display: 'flex',
            gap: 0.5
          }}>
            {(vehicleOpenTickets.length + vehicleAcknowledgedTickets.length) > 0 && (() => {
              // Un solo chip con el total. El color predomina según haya más abiertos o vistos.
              const openCount = vehicleOpenTickets.length;
              const ackCount = vehicleAcknowledgedTickets.length;
              const total = openCount + ackCount;
              const isOpenPredominant = openCount >= ackCount;
              const chipColor = isOpenPredominant ? 'error' : 'warning';
              const tooltipParts = [];
              if (openCount > 0) tooltipParts.push(t('hardcoded.components_schematics_vehicleschematiccard.opencount_abiertoopencount1s', { openCount: openCount, openCount1s: openCount > 1 ? 's' : '' }));
              if (ackCount > 0) tooltipParts.push(t('hardcoded.components_schematics_vehicleschematiccard.ackcount_vistoackcount1s', { ackCount: ackCount, ackCount1s: ackCount > 1 ? 's' : '' }));
              const tooltip = t('hardcoded.components_schematics_vehicleschematiccard.tooltippartsjoi_clic_para_ver', { tooltipPartsjoi: tooltipParts.join(' · ') });
              return (
                <Tooltip title={tooltip} arrow>
                  <Chip
                    label={total}
                    size="small"
                    icon={<TicketIcon />}
                    color={chipColor}
                    clickable
                    onClick={(e) => {
                      e.stopPropagation();
                      setTicketsDrawerOpen(true);
                    }}
                    sx={{
                      height: 20,
                      fontSize: '0.7rem',
                      cursor: 'pointer',
                      animation: (shouldPlaySound && openCount > 0) ? 'blink 1s infinite' : 'none'
                    }}
                  />
                </Tooltip>
              );
            })()}
          </Box>
        )}

        {/* HEADER */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          mb: 1.5,
          position: 'relative',
          minHeight: 32
        }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            mb: 0.5
          }}>
            <Typography
              variant="h6"
              fontWeight={600}
              color="primary.main"
              sx={{
                fontSize: { xs: '0.7rem', sm: '0.85rem' },
                lineHeight: 1.2,
                textAlign: 'center',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}
            >
              {vehicle.display_name || vehicle.alias || t('hardcoded.components_schematics_vehicleschematiccard.vehicle_id', { id: vehicle.id })}
              {/* {displayMode !== 'pressure' && (
                <Typography
                  component="span"
                  variant="caption"
                  sx={{
                    ml: 0.5,
                    color: displayMode === 'temperature' ? 'warning.main' : 'secondary.main',
                    fontWeight: 500,
                    fontSize: '0.6rem'
                  }}
                >
                  ({displayMode === 'temperature' ? 'TEMP' : 'HR'})
                </Typography>
              )} */}
            </Typography>
          </Box>

          {showCompanyTag && vehicle.company_name && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mb: 0.5 }}>
              <Chip
                label={`${vehicle.company_name}`}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  fontSize: '0.65rem',
                  maxWidth: 180,
                  '& .MuiChip-label': {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    display: 'block'
                  }
                }}
              />
            </Box>
          )}

          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            width: '100%',
            px: 0.5
          }}>
            {/* Status Dot */}
            <Tooltip title={getStatusTooltipText()} arrow placement="left" enterDelay={200}>
              <Box
                onClick={handleStatusDotClick}
                sx={{
                  width: { xs: 14, sm: 16 },
                  height: { xs: 14, sm: 16 },
                  borderRadius: '50%',
                  bgcolor: STATUS_DOT_COLORS[status],
                  border: '2px solid white',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                  cursor: hasAssignedReceivers ? 'pointer' : 'default',
                  transition: 'all 0.2s ease',
                  animation: status === 'online' ? 'status-pulse 2s infinite' :
                    status === 'sin_receptor' && shouldPlaySound ? 'blink 1s infinite' : 'none',
                  flexShrink: 0,
                  '&:hover': {
                    transform: hasAssignedReceivers ? 'scale(1.3)' : 'none',
                    boxShadow: hasAssignedReceivers ? '0 2px 6px rgba(0,0,0,0.4)' : '0 1px 3px rgba(0,0,0,0.3)',
                  }
                }} />
            </Tooltip>

            {/* Mute Button */}
            <Box sx={{
              display: 'flex',
              gap: 0.5,
              alignItems: 'center',
              justifyContent: 'center',
              flex: 1
            }}>
              {((vehicleOpenTickets.length > 0) || (isVehicleMuted && vehicleAcknowledgedTickets.length > 0)) && onToggleVehicleMute && (
                <Tooltip
                  title={
                    globalMute
                      ? t('hardcoded.components_schematics_vehicleschematiccard.silencio_global_activo')
                      : isVehicleMuted
                        ? t('hardcoded.components_schematics_vehicleschematiccard.activar_sonido_de_este_vehiculo')
                        : t('hardcoded.components_schematics_vehicleschematiccard.silenciar_sonido_de_este_vehiculo')
                  }
                  arrow
                  placement="top"
                >
                  <IconButton
                    size="small"
                    onClick={handleToggleMute}
                    disabled={globalMute}
                    sx={{
                      width: { xs: 14, sm: 16 },
                      height: { xs: 14, sm: 16 },
                      padding: 0,
                      bgcolor: vehicleOpenTickets.length > 0 ? 'error.main' :
                        isVehicleMuted ? 'warning.main' : 'error.main',
                      border: '2px solid white',
                      borderRadius: '50%',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                      flexShrink: 0,
                      opacity: globalMute ? 0.5 : 1,
                      '&:hover': {
                        bgcolor: globalMute ? 'warning.main' :
                          vehicleOpenTickets.length > 0 ? 'error.dark' :
                            (isVehicleMuted ? 'warning.dark' : 'error.dark'),
                        transform: globalMute ? 'none' : 'scale(1.3)',
                        boxShadow: globalMute ? '0 1px 3px rgba(0,0,0,0.3)' : '0 2px 6px rgba(0,0,0,0.4)',
                      }
                    }}
                  >
                    {(isVehicleMuted || vehicleAcknowledgedTickets.length > 0) && vehicleOpenTickets.length === 0 ? (
                      <MuteIcon sx={{
                        fontSize: { xs: 7, sm: 8 },
                        color: 'white'
                      }} />
                    ) : (
                      <UnmuteIcon sx={{
                        fontSize: { xs: 7, sm: 8 },
                        color: 'white'
                      }} />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Map Button */}
            <IconButton
              size="small"
              onClick={() => setMapModalOpen(true)}
              sx={{
                width: { xs: 14, sm: 16 },
                height: { xs: 14, sm: 16 },
                padding: 0,
                bgcolor: 'primary.main',
                border: '2px solid white',
                borderRadius: '50%',
                boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                flexShrink: 0,
                '&:hover': {
                  bgcolor: 'primary.dark',
                  transform: 'scale(1.3)',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
                }
              }}
            >
              <WorldIcon sx={{
                fontSize: { xs: 7, sm: 8 },
                color: 'white'
              }} />
            </IconButton>
          </Box>
        </Box>

        {/* SCHEMATIC LAYOUT */}
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: 0.3, sm: 0.5, md: 0.6 },
          mb: 1.5,
          minHeight: { xs: 60, sm: 70 },
          overflow: 'hidden',
          width: '100%',
          px: { xs: 0.3, sm: 0.5 }
        }}>
          {layout.map((row, rowIndex) => {
            return (
              <Box
                key={`vehicle-${vehicle.id}-row-${rowIndex}`}
                sx={{
                  display: 'flex',
                  gap: { xs: 0.25, sm: 0.3, md: 0.4 },
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                  width: 'fit-content',
                  maxWidth: '100%',
                  ...(row.length === 1 && {
                    justifyContent: 'center'
                  }),
                  ...(row.length === 2 && {
                    width: { xs: '100px', sm: '120px' },
                    justifyContent: 'space-between'
                  }),
                  ...(row.length === 3 && {
                    width: { xs: '120px', sm: '140px' },
                    justifyContent: 'space-between'
                  }),
                  ...(row.length === 4 && {
                    width: { xs: '140px', sm: '160px' },
                    justifyContent: 'space-between'
                  }),
                  ...(row.length > 4 && {
                    width: { xs: '180px', sm: '200px' },
                    justifyContent: 'space-evenly',
                    flexWrap: 'wrap'
                  })
                }}
              >
                {(() => {
                  // Renderiza el row agrupando pares Dual adyacentes dentro de
                  // un wrapper común que pinta UN solo delineado externo, sin afectar
                  // el layout interno ni el tamaño de los tiles individuales.
                  const out = [];
                  let i = 0;
                  while (i < row.length) {
                    const position = row[i];
                    const nextPosition = row[i + 1];
                    const sameYoyoGroup = nextPosition != null && yoyoGroups.some(
                      (g) => g.includes(position) && g.includes(nextPosition)
                    );

                    const tileFor = (pos) => {
                      const positionTickets = vehicleTickets.filter(t => t.position === pos);
                      const positionAlarmStatuses = alarmStatuses.filter(s =>
                        s.vehicle_id === vehicle.id && s.position === pos
                      );
                      return (
                        <StyledPositionTile
                          key={t('hardcoded.components_schematics_vehicleschematiccard.vehicle-id-position-pos', { id: vehicle.id, pos: pos })}
                          vehicle={vehicle}
                          position={pos}
                          realtimeData={realtimeData[pos]}
                          lastAvailableData={lastAvailableData[pos]}
                          alarms={alarms.filter(alarm =>
                            vehicle.vehicle_sensors?.some(vs =>
                              vs.position === pos &&
                              alarm.metric_type === (vs.sensor_type === 'PSI' ? 'PSI' : vs.sensor_type)
                            )
                          )}
                          alarmStatuses={positionAlarmStatuses}
                          positionTickets={positionTickets}
                          onTelemetryClick={onTelemetryClick}
                          displayMode={displayMode}
                          coldMode={coldMode}
                          coldPosition={coldPlan?.positions?.[String(pos)]}
                          // Cuando el tile está en un par Yoyo agrupado, ya NO debe
                          // pintar su propio delineado individual: lo hace el wrapper.
                          suppressYoyoOutline={sameYoyoGroup || (i > 0 && yoyoGroups.some(g => g.includes(pos) && g.includes(row[i - 1])))}
                        />
                      );
                    };

                    if (sameYoyoGroup) {
                      out.push(
                        <Box
                          key={`yoyo-pair-${vehicle.id}-${position}-${nextPosition}`}
                          sx={{
                            display: 'inline-flex',
                            gap: 0.25,
                            padding: '4px',
                            borderRadius: 1,
                            // Delineado conjunto que abarca AMBOS tiles del par.
                            boxShadow: (t) => `inset 0 0 0 1px ${t.palette.common.black}, inset 0 0 0 4px ${t.palette.error.main}`,
                          }}
                        >
                          {tileFor(position)}
                          {tileFor(nextPosition)}
                        </Box>
                      );
                      i += 2;
                    } else {
                      out.push(tileFor(position));
                      i += 1;
                    }
                  }
                  return out;
                })()}
              </Box>
            );
          })}
        </Box>
      </Paper>

      <VehicleRouteMapModal
        open={mapModalOpen}
        vehicle={vehicle}
        onClose={() => setMapModalOpen(false)}
        realtimeData={realtimeData}
      />

      <ReceiverEventsModal
        open={receiverEventsModalOpen}
        onClose={() => setReceiverEventsModalOpen(false)}
        receptores={vehicle.receivers || []}
      />

      <VehicleTicketsDrawer
        open={ticketsDrawerOpen}
        onClose={() => setTicketsDrawerOpen(false)}
        vehicle={vehicle}
        openTickets={vehicleOpenTickets}
        acknowledgedTickets={vehicleAcknowledgedTickets}
        onTicketsChanged={() => refreshActiveTickets?.()}
      />
    </>
  );
};

export default VehicleSchematicCard;